import { errorTrend, type TurnLike } from '@core/progress/errorTrend';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * A child's mistakes over their recent turns — م-3-02's منحنى الأخطاء.
 *
 * The line is the illustration, not the answer: the direction is also said
 * in words, because a shape is unreadable to a screen reader and to anyone
 * who does not read charts. Time runs the way the page reads — right to left
 * in Arabic — so the eye meets the oldest turn first in both languages.
 */

/** The drawing's own coordinate space; CSS scales it to whatever width it gets. */
const W = 100;
const H = 100;
const PAD = 6;

export function ErrorCurve({ turns }: { turns: TurnLike[] }) {
  const { t, dir } = useLanguage();
  const { points, direction, worst } = errorTrend(turns);

  if (points.length < 2) {
    return (
      <p data-testid="curve-empty" className="rounded-[14px] bg-surface p-4 text-center text-sm text-helper">
        {t('children.trendUnknown')}
      </p>
    );
  }

  const step = (W - PAD * 2) / (points.length - 1);
  const placed = points.map((p, i) => {
    const along = PAD + i * step;
    return {
      ...p,
      // Oldest first along the reading direction, so Arabic starts at the right.
      x: dir === 'rtl' ? W - along : along,
      // worst is never zero, so a run of clean turns lands flat on the floor
      // rather than dividing by it.
      y: H - PAD - (p.errors / worst) * (H - PAD * 2),
    };
  });

  const label = `${t('children.errorCurve')} — ${t(`children.trend${
    direction === 'better' ? 'Better' : direction === 'worse' ? 'Worse' : 'Steady'
  }`)}`;

  return (
    <div className="space-y-2">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={label}
        data-testid="error-curve"
        preserveAspectRatio="none"
        className="h-28 w-full"
      >
        <polyline
          points={placed.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinejoin="round"
          className="text-primary"
          vectorEffect="non-scaling-stroke"
        />
        {/*
          * A dot, drawn as a zero-length round-capped line. The box is
          * stretched to whatever width the card gives it, which turns a
          * <circle> into an ellipse — a stroke marked non-scaling is immune,
          * so these stay round at any width.
          */}
        {placed.map((p) => (
          <line
            key={p.id}
            data-testid={`curve-point-${p.id}`}
            x1={p.x}
            y1={p.y}
            x2={p.x}
            y2={p.y}
            strokeWidth={6}
            strokeLinecap="round"
            stroke="currentColor"
            className={p.errors > 0 ? 'text-danger' : 'text-primary'}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      <p
        data-testid="curve-direction"
        className={`text-center text-sm font-medium ${direction === 'worse' ? 'text-danger' : 'text-primary'}`}
      >
        {t(`children.trend${direction === 'better' ? 'Better' : direction === 'worse' ? 'Worse' : 'Steady'}`)}
      </p>
    </div>
  );
}
